import React, { useState, useEffect } from 'react';
import Seo from './common/Seo';
import FeaturedServiceLinks from './common/FeaturedServiceLinks';
import { useLocation } from 'react-router-dom';
import { 
  FaHandHoldingMedical, 
  FaSyringe, 
  FaHeartbeat, 
  FaWhatsapp, 
  FaClock,
  FaUserMd,
  FaStar,
  FaShieldAlt,
  FaArrowRight,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaMedkit
} from 'react-icons/fa';

import { COMPANY_INFO, COVERAGE_AREAS } from '../constants';
import { TESTIMONIALS } from '../data';

import cuidadoras from '../assets/cuidadoras.jpg';
import auxiliares from '../assets/auxiliar.jpg';
import generales from '../assets/general.jpg';
import suero from '../assets/suero.jpg';

const ServiceSection = ({
  id,
  title,
  image,
  alt,
  description,
  features,
  icon: Icon,
  reverse,
  priceInfo
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById(id);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [id]);

  const handleWhatsAppClick = () => {
    const message = `Hola, estoy interesado en el servicio de ${title.toLowerCase()}. ¿Podrían proporcionarme más información?`;
    window.open(
      `https://wa.me/${COMPANY_INFO.whatsapp}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section
      id={id}
      className={`relative py-20 scroll-mt-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${reverse ? 'bg-gradient-to-r from-blue-50 to-white' : 'bg-white'}`}
      aria-labelledby={`${id}-heading`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-16 items-center ${reverse ? 'lg:grid-flow-dense' : ''}`}>
          {/* Imagen */}
          <div className={`relative ${reverse ? 'lg:col-start-2' : ''}`}>
            <div className="overflow-hidden rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 group">
              <img
                src={image}
                alt={alt}
                className="w-full h-96 object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <span className="text-white font-medium">Ver detalles</span>
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className={`${reverse ? 'lg:col-start-1' : ''}`}>
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
                  <Icon className="w-8 h-8 text-blue-600" />
                </div>
                <h2 id={`${id}-heading`} className="text-4xl font-bold text-gray-900">
                  {title}
                </h2>
              </div>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">{description}</p>

              {priceInfo && (
                <div className="bg-blue-50 rounded-xl p-4 mb-8 border border-blue-100">
                  <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                    <FaShieldAlt className="mr-2" /> Un plan hecho a tu medida
                  </h3>
                  <p className="text-gray-700">{priceInfo}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                    <span className="shrink-0 mt-1 text-blue-600 bg-blue-50 p-1 rounded-full">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleWhatsAppClick}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-3 group flex-1"
                >
                  <FaWhatsapp className="w-6 h-6" />
                  <span>Solicitar por WhatsApp</span>
                  <FaArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 flex-1">
                  <FaPhoneAlt className="w-5 h-5" />
                  <span>Llamar ahora</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const VideoBackground = () => {
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);

  if (location.pathname !== '/services') return null;

  return (
    <div className="absolute inset-0 z-0 h-[500px] overflow-hidden">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className={`absolute inset-0 w-full h-full object-cover opacity-95 transition-opacity duration-1000 ${isLoaded ? 'opacity-95' : 'opacity-0'}`}
        onLoadedData={() => setIsLoaded(true)}
      >
        <source src={`${process.env.PUBLIC_URL}/videos/geriatria-serena.mp4`} type="video/mp4" />
        Tu navegador no soporta videos HTML5.
      </video>
      <div className="absolute bg-gradient-to-b from-blue-900/70 to-blue-800/50 w-full h-full" />
      
      {/* Mostrar imagen de placeholder mientras carga el video */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-blue-800 flex items-center justify-center">
          <div className="text-white text-center">
            <FaHeartbeat className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Cargando video...</p>
          </div>
        </div>
      )}
    </div>
  );
};

const TestimonialSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Testimonios de Nuestros Clientes</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            La satisfacción de nuestros pacientes y sus familias es nuestra mayor recompensa
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 italic mb-6">"{testimonial.content}"</p>
              <div className="flex items-center">
                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                  <FaUserMd className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                  <p className="text-gray-600 text-sm">{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CoverageSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Áreas de Cobertura</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Brindamos nuestros servicios en toda el área metropolitana de Guadalajara
          </p>
        </div>

        <div className="bg-blue-50 rounded-2xl p-8 shadow-inner">
          <div className="flex items-center justify-center mb-6">
            <FaMapMarkerAlt className="text-blue-600 w-8 h-8 mr-3" />
            <h3 className="text-2xl font-semibold text-gray-800">Zonas que atendemos</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {COVERAGE_AREAS.map((area, index) => (
              <div key={index} className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                <span className="font-medium text-gray-800">{area}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-700">
              ¿No ves tu zona? <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} className="text-blue-600 font-semibold hover:underline">Contáctanos</a> para verificar cobertura.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

function ServicesPage() {
  const services = [
    {
      id: 'cuidadoras',
      title: 'Cuidadoras a domicilio en Guadalajara',
      image: cuidadoras,
      alt: 'Cuidadora brindando atención personalizada a adulto mayor en Guadalajara',
      description: 'Nuestras cuidadoras profesionales ofrecen asistencia diaria, compañía especializada y monitoreo de salud para adultos mayores y pacientes en recuperación postoperatoria en Guadalajara y área metropolitana.',
      features: [
        'Asistencia en actividades de la vida diaria',
        'Acompañamiento terapéutico y apoyo emocional',
        'Asistencia en su vida diaria',
        'Prevención de caídas y accidentes domésticos',
        'Terapia ocupacional',
        'Empatía, responsabilidad y puntualidad garantizada'
      ],
      icon: FaHandHoldingMedical,
      priceInfo: "Ajustamos horas, días y nivel de cuidado a lo que tu familiar necesita, sin cargos ocultos ni sorpresas. Cotización personalizada por WhatsApp."
    },
    {
      id: 'auxiliares',
      title: 'Auxiliar de Enfermería a domicilio',
      image: auxiliares,
      alt: 'Auxiliar de enfermería asistiendo en procedimiento médico a domicilio',
      description: 'Apoyo profesional especializado en cuidados básicos y clínicos, trabajando bajo supervisión de enfermeras tituladas para garantizar un servicio seguro y de calidad en el hogar.',
      features: [
        'Procedimientos básicos de enfermería',
        'Manejo y esterilización de equipos médicos',
        'Administración de medicamentos prescritos',
        'Terapias y curaciones básicas',
        'Comunicación efectiva con familiares y médicos',
        'Toma de muestras y registro de signos vitales'
      ],
      icon: FaSyringe,
      priceInfo: "Siempre bajo supervisión de enfermeras tituladas, con protocolos claros para cada procedimiento. Cotización personalizada por WhatsApp."
    },
    {
      id: 'enfermeria-general',
      title: 'Enfermería General a domicilio',
      image: generales,
      alt: 'Enfermera titulada brindando atención médica especializada en casa',
      description: 'Atención integral por personal titulado y con experiencia, seguimiento clínico de tratamientos y coordinación directa con familiares y especialistas médicos.',
      features: [
        'Valoración y seguimiento del estado de salud completo',
        'Elaboración de planes de cuidado personalizados',
        'Manejo de equipos médicos avanzados',
        'Informes detallados para familiares y médicos',
        'Administración de tratamientos especializados',
        'Coordinación con servicios médicos de emergencia'
      ],
      icon: FaHeartbeat,
      priceInfo: "Personal titulado con cédula profesional vigente, para los casos que requieren mayor nivel clínico. Cotización personalizada por WhatsApp."
    },
    {
      id: 'sueros-aplicaciones',
      title: 'Sueros y aplicaciones a domicilio',
      image: suero,
      alt: 'Enfermera aplicando suero a paciente en casa',
      description: 'Aplicación de sueros, inyecciones y medicamentos de manera segura en tu hogar, realizada por personal de enfermería certificado y con todas las medidas de bioseguridad.',
      features: [
        'Aplicación de sueros intravenosos',
        'Inyecciones intramusculares y subcutáneas',
        'Administración de medicamentos prescritos',
        'Supervisión y monitoreo durante el procedimiento',
        'Atención inmediata en la comodidad de tu hogar',
        'Seguridad y profesionalismo garantizado'
      ],
      icon: FaMedkit,
      priceInfo: "Aplicación segura en tu hogar por personal certificado, con todas las medidas de bioseguridad. Cotización personalizada por WhatsApp."
    }
  ];

  return (
    <main className="overflow-hidden relative">
      <Seo
        title="Servicios de Enfermería y Cuidado Geriátrico a Domicilio | Geriatría Serena"
        description="Servicios profesionales de cuidadoras, auxiliares y enfermeras a domicilio en Guadalajara y Zapopan. Atención humana y médica de alta calidad."
        keywords="cuidadoras Guadalajara, enfermería a domicilio Zapopan, cuidado de adultos mayores, atención postoperatoria, auxiliares de enfermería, geriatría"
        canonical="https://www.geriatriaserena.com/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Geriatría Serena",
          "description": "Servicios de enfermería y cuidado geriátrico a domicilio en Guadalajara y área metropolitana",
          "image": "https://www.geriatriaserena.com/images/logo.svg",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Lago Ginebra 2732, Patria",
            "addressLocality": "Zapopan",
            "addressRegion": "Jalisco",
            "postalCode": "45160",
            "addressCountry": "MX"
          },
          "telephone": "+52-33-2545-6522",
          "url": "https://www.geriatriaserena.com",
          "priceRange": "$$",
          "areaServed": ["Guadalajara", "Zapopan", "Tlaquepaque", "Tonalá", "Tlajomulco", "El Salto"],
          "serviceType": ["Nursing care", "Elderly care", "Home health care"],
          "hasCredential": ["Registered Nurse", "Nursing Assistant", "Caregiver"]
        }}
        meta={[
          { property: 'og:title', content: 'Servicios de Enfermería y Cuidadoras a Domicilio - Geriatría Serena' },
          { property: 'og:description', content: 'Atención profesional de enfermería y cuidado geriátrico en Guadalajara y Zapopan. Calidad, confianza y humanidad.' },
          { property: 'og:image', content: 'https://www.geriatriaserena.com/images/banners/banner1-large.webp' },
          { property: 'og:url', content: 'https://www.geriatriaserena.com/services' },
          { property: 'og:type', content: 'website' },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'Servicios de Enfermería y Cuidadoras a Domicilio - Geriatría Serena' },
          { name: 'twitter:description', content: 'Cuidado profesional y humano para adultos mayores y pacientes en recuperación.' },
          { name: 'twitter:image', content: 'https://www.geriatriaserena.com/images/banners/banner1-large.webp' }
        ]}
      />

      <div className="pt-32 pb-8 text-center relative h-[500px] flex items-center justify-center">
        <VideoBackground />
        <div className="relative z-10 px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 drop-shadow-2xl">
            Servicios Profesionales de Enfermería
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
            Excelencia médica y cuidado humano a domicilio en Guadalajara y área metropolitana
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href={`https://wa.me/${COMPANY_INFO.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center gap-2"
            >
              <FaWhatsapp className="w-5 h-5" />
              Contactar por WhatsApp
            </a>
            <button className="bg-white hover:bg-gray-100 text-blue-600 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center gap-2">
              <FaPhoneAlt className="w-5 h-5" />
              Llamar ahora
            </button>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaShieldAlt className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Profesionales Certificados</h3>
              <p className="text-blue-100">Personal capacitado y con certificaciones vigentes</p>
            </div>
            
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaClock className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Disponibilidad 24/7</h3>
              <p className="text-blue-100">Servicio continuo todos los días del año</p>
            </div>
            
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaStar className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Calidad Garantizada</h3>
              <p className="text-blue-100">Protocolos establecidos y supervisión constante</p>
            </div>
            
            <div className="text-center">
              <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaHeartbeat className="w-8 h-8" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Atención Personalizada</h3>
              <p className="text-blue-100">Planes de cuidado adaptados a cada paciente</p>
            </div>
          </div>
        </div>
      </section>

      {services.map((service, index) => (
        <ServiceSection
          key={service.id}
          {...service}
          reverse={index % 2 !== 0}
        />
      ))}

      <FeaturedServiceLinks title="Servicios especializados por necesidad" />
      <TestimonialSection />
      <CoverageSection />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">¿Necesitas Ayuda para Elegir el Servicio Adecuado?</h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
            Nuestros asesores están disponibles para orientarte y recomendarte el mejor servicio según tus necesidades específicas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`https://wa.me/${COMPANY_INFO.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-semibold transition-colors duration-300 flex items-center justify-center gap-3"
            >
              <FaWhatsapp className="w-6 h-6" />
              Chatear con un asesor
            </a>
            <button className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-xl font-semibold transition-colors duration-300 flex items-center justify-center gap-3">
              <FaPhoneAlt className="w-5 h-5" />
              Llamar para asesoría
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ServicesPage;